import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ShiftProvider } from './contexts/ShiftContext';
import { SessionProvider } from './contexts/SessionContext';
import { HoldToRecordProvider } from './contexts/HoldToRecordContext';
import { AdmissionAmbientProvider } from './contexts/AdmissionAmbientContext';
import { ROUTES } from './constants/routes';
import SttWarmupBridge from './components/SttWarmupBridge';
import LoginScreen from './screens/LoginScreen';
import PatientsListScreen from './screens/PatientsListScreen';
import PatientDetailScreen from './screens/PatientDetailScreen';
import RecordingScreen from './screens/RecordingScreen';
import GeneratingScreen from './screens/GeneratingScreen';
import NoteReviewScreen from './screens/NoteReviewScreen';
import NotesListScreen from './screens/NotesListScreen';
import NoteDetailScreen from './screens/NoteDetailScreen';
import VitalsListScreen from './screens/VitalsListScreen';
import VitalDetailScreen from './screens/VitalDetailScreen';
import ReportsListScreen from './screens/ReportsListScreen';
import ReportDetailScreen from './screens/ReportDetailScreen';
import MedicationsListScreen from './screens/MedicationsListScreen';
import MedicationDetailScreen from './screens/MedicationDetailScreen';
import FluidBalanceListScreen from './screens/FluidBalanceListScreen';
import FluidBalanceDetailScreen from './screens/FluidBalanceDetailScreen';
import ProfileScreen from './screens/ProfileScreen';
import ShiftHandoverScreen from './screens/ShiftHandoverScreen';
import ShiftHandoverGeneratingScreen from './screens/ShiftHandoverGeneratingScreen';
import ShiftHandoverVoiceScreen from './screens/ShiftHandoverVoiceScreen';
import ShiftHandoverVoiceGeneratingScreen from './screens/ShiftHandoverVoiceGeneratingScreen';
import AdmissionRecordingRoute from './screens/AdmissionRecordingRoute';
import OnboardingScreen from './screens/OnboardingScreen';
import AccessGate from './components/AccessGate';
import AccessCodeScreen from './screens/AccessCodeScreen';
import DevAutoLogin from './components/DevAutoLogin';
import PatientSelectScreen from './screens/PatientSelectScreen';
import JoinScreen from './screens/JoinScreen';
import ChangePasswordScreen from './screens/ChangePasswordScreen';
import ConsentModal from './components/ConsentModal';
import { UsageProvider, UsageToasts } from './contexts/UsageContext';
import { isTutorialCompleted } from './utils/tutorialStorage';
import { isPublicProductionHost } from './utils/publicHost';
import DoctorApp from './doctor/DoctorApp';

function ProtectedRoute({ children }) {
  const { isAuthenticated, mustChangePassword, needsConsent, consentStatusReady, accountType } = useAuth();
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  if (accountType === 'doctor' && !isPublicProductionHost()) {
    return <Navigate to={ROUTES.DOCTOR_PATIENTS} replace />;
  }
  if (!consentStatusReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (mustChangePassword) return <ChangePasswordScreen />;
  if (needsConsent) return <ConsentModal />;
  return children;
}

function PostAuthRedirect() {
  const { accountType } = useAuth();
  if (accountType === 'doctor' && !isPublicProductionHost()) {
    return <Navigate to={ROUTES.DOCTOR_PATIENTS} replace />;
  }
  const target = isTutorialCompleted() ? ROUTES.PATIENTS : ROUTES.ONBOARDING;
  return <Navigate to={target} replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const isDoctorPath = location.pathname.startsWith('/doctor');

  const hospitalRoutes = useRoutes([
    { path: ROUTES.ACCESS_CODE, element: <AccessCodeScreen /> },
    { path: ROUTES.JOIN, element: <JoinScreen /> },
    { path: ROUTES.LOGIN, element: isAuthenticated ? <PostAuthRedirect /> : <LoginScreen /> },
    {
      path: '/',
      element: <ProtectedRoute><PostAuthRedirect /></ProtectedRoute>,
    },
    { path: ROUTES.PATIENTS, element: <ProtectedRoute><PatientsListScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_DETAIL, element: <ProtectedRoute><PatientDetailScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_PRE_RECORD, element: <ProtectedRoute><RecordingScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_RECORD, element: <ProtectedRoute><RecordingScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_ADMISSION, element: <ProtectedRoute><AdmissionRecordingRoute /></ProtectedRoute> },
    { path: ROUTES.PATIENT_GENERATING, element: <ProtectedRoute><GeneratingScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_REVIEW, element: <ProtectedRoute><NoteReviewScreen /></ProtectedRoute> },
    { path: ROUTES.RECORD, element: <ProtectedRoute><RecordingScreen /></ProtectedRoute> },
    { path: ROUTES.FREE_RECORD, element: <ProtectedRoute><RecordingScreen /></ProtectedRoute> },
    { path: ROUTES.GENERATING, element: <ProtectedRoute><GeneratingScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_SELECT, element: <ProtectedRoute><PatientSelectScreen /></ProtectedRoute> },
    { path: ROUTES.REVIEW, element: <ProtectedRoute><NoteReviewScreen /></ProtectedRoute> },
    { path: ROUTES.NOTES, element: <ProtectedRoute><NotesListScreen /></ProtectedRoute> },
    { path: ROUTES.NOTE_DETAIL, element: <ProtectedRoute><NoteDetailScreen /></ProtectedRoute> },
    { path: ROUTES.VITALS, element: <ProtectedRoute><VitalsListScreen /></ProtectedRoute> },
    { path: ROUTES.VITAL_DETAIL, element: <ProtectedRoute><VitalDetailScreen /></ProtectedRoute> },
    { path: ROUTES.REPORTS, element: <ProtectedRoute><ReportsListScreen /></ProtectedRoute> },
    { path: ROUTES.REPORT_DETAIL, element: <ProtectedRoute><ReportDetailScreen /></ProtectedRoute> },
    { path: ROUTES.MEDICATIONS, element: <ProtectedRoute><MedicationsListScreen /></ProtectedRoute> },
    { path: ROUTES.MEDICATION_DETAIL, element: <ProtectedRoute><MedicationDetailScreen /></ProtectedRoute> },
    { path: ROUTES.FLUID_BALANCE, element: <ProtectedRoute><FluidBalanceListScreen /></ProtectedRoute> },
    { path: ROUTES.PATIENT_FLUID_BALANCE, element: <ProtectedRoute><FluidBalanceDetailScreen /></ProtectedRoute> },
    { path: ROUTES.PROFILE, element: <ProtectedRoute><ProfileScreen /></ProtectedRoute> },
    { path: ROUTES.ONBOARDING, element: <ProtectedRoute><OnboardingScreen /></ProtectedRoute> },
    { path: ROUTES.SHIFT_HANDOVER_GENERATING, element: <ProtectedRoute><ShiftHandoverGeneratingScreen /></ProtectedRoute> },
    { path: ROUTES.SHIFT_HANDOVER, element: <ProtectedRoute><ShiftHandoverScreen /></ProtectedRoute> },
    { path: ROUTES.SHIFT_HANDOVER_VOICE, element: <ProtectedRoute><ShiftHandoverVoiceScreen /></ProtectedRoute> },
    { path: ROUTES.SHIFT_HANDOVER_VOICE_GENERATING, element: <ProtectedRoute><ShiftHandoverVoiceGeneratingScreen /></ProtectedRoute> },
    { path: '*', element: <Navigate to={ROUTES.PATIENTS} replace /> },
  ]);

  if (isDoctorPath) {
    if (isPublicProductionHost()) {
      return <Navigate to={ROUTES.LOGIN} replace />;
    }
    return <DoctorApp />;
  }
  return hospitalRoutes;
}

export default function App() {
  return (
    <AccessGate>
      <AuthProvider>
        <DevAutoLogin>
          <LanguageProvider>
          <ShiftProvider>
            <SessionProvider>
              <UsageProvider>
                <HoldToRecordProvider>
                  <AdmissionAmbientProvider>
                    <SttWarmupBridge />
                    <UsageToasts />
                    <AppRoutes />
                  </AdmissionAmbientProvider>
                </HoldToRecordProvider>
              </UsageProvider>
            </SessionProvider>
          </ShiftProvider>
        </LanguageProvider>
        </DevAutoLogin>
      </AuthProvider>
    </AccessGate>
  );
}
